import type { ApiClient } from '../client'
import type {
  CreateLocationRequest,
  InvitedUser,
  InviteUserRequest,
  ListUsersQuery,
  Location,
  Notification,
  Organization,
  PublicBranding,
  UpdateLocationRequest,
  UpdateOrganizationRequest,
  UpdateUserRequest,
  User,
} from '../types'

/** GET /v1/branding/resolve?host= — public (chokepoint exemption), display fields only. */
export function brandingEndpoints(api: ApiClient) {
  return {
    resolve: (host?: string) =>
      api.get<PublicBranding>('branding/resolve', { auth: false, query: { host } }),
  }
}

/** src/modules/tenancy/locations/controllers/locations.controller.ts */
export function locationsEndpoints(api: ApiClient) {
  return {
    list: (active: 'true' | 'false' | 'all' = 'true') =>
      api.get<readonly Location[]>('locations', { query: { active } }),
    get: (id: string) => api.get<Location>(`locations/${id}`),
    create: (body: CreateLocationRequest) => api.post<Location>('locations', body),
    update: (id: string, body: UpdateLocationRequest) =>
      api.patch<Location>(`locations/${id}`, body),
    /** Soft delete — `active` becomes false. */
    deactivate: (id: string) =>
      api.delete<{ readonly id: string; readonly active: false }>(`locations/${id}`),
  }
}

/** src/modules/tenancy/organizations/controllers/organizations.controller.ts */
export function organizationsEndpoints(api: ApiClient) {
  return {
    get: (id: string) => api.get<Organization>(`organizations/${id}`),
    update: (id: string, body: UpdateOrganizationRequest) =>
      api.patch<Organization>(`organizations/${id}`, body),
    /** Separate endpoint by design: switching Standalone ↔ Layer changes system-wide behaviour. */
    setCalendarSource: (id: string, calendar_source: string) =>
      api.patch<Organization>(`organizations/${id}/calendar-source`, { calendar_source }),
  }
}

/** src/modules/tenancy/users/controllers/users.controller.ts */
export function usersEndpoints(api: ApiClient) {
  return {
    list: (query: ListUsersQuery = {}) =>
      api.get<readonly User[]>('users', { query: { ...query } }),
    get: (id: string) => api.get<User>(`users/${id}`),
    /** Returns the raw invite token exactly once (no mailer, FRONTEND_PLAN BE-10). */
    invite: (body: InviteUserRequest) => api.post<InvitedUser>('users', body),
    update: (id: string, body: UpdateUserRequest) => api.patch<User>(`users/${id}`, body),
    disable: (id: string) => api.post<User>(`users/${id}/disable`),
    enable: (id: string) => api.post<User>(`users/${id}/enable`),
    resendInvite: (id: string) =>
      api.post<{ readonly invite_token: string }>(`users/${id}/resend-invite`),
  }
}

export interface ListNotificationsQuery {
  readonly read?: boolean
  readonly severity?: 'info' | 'warn' | 'critical'
  /** ≤ 100 (notification.dto.ts) */
  readonly limit?: number
}

/** src/modules/notifications/controllers/notifications.controller.ts */
export function notificationsEndpoints(api: ApiClient) {
  return {
    list: (query: ListNotificationsQuery = {}) =>
      api.get<readonly Notification[]>('notifications', {
        query: {
          read: query.read === undefined ? undefined : String(query.read),
          severity: query.severity,
          limit: query.limit,
        },
      }),
    unreadCount: () => api.get<{ readonly unread: number }>('notifications/unread-count'),
    markRead: (id: string) => api.post<Notification>(`notifications/${id}/read`),
    markAllRead: () => api.post<{ readonly marked: number }>('notifications/read-all'),
  }
}
